import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { Refund, RefundProcessParams } from '@/schemas/supplier'

interface RefundProcessDialogProps {
  refund: Refund
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (data: RefundProcessParams) => void
  isLoading?: boolean
}

export function RefundProcessDialog({
  refund,
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: RefundProcessDialogProps) {
  const [action, setAction] = useState<1 | 2>(1) // 1=同意 2=拒绝
  const [refundPrice, setRefundPrice] = useState(refund.refund_price.toString())
  const [refuseReason, setRefuseReason] = useState('')

  // 验证表单
  const isValid = () => {
    if (!refundPrice || parseFloat(refundPrice) <= 0) {
      return false
    }
    if (action === 2 && !refuseReason.trim()) {
      return false
    }
    return true
  }

  // 确认处理
  const handleConfirm = () => {
    if (!isValid()) return

    const data: RefundProcessParams = {
      refund_price: parseFloat(refundPrice),
      type: action,
      refuse_reason: action === 2 ? refuseReason : undefined,
    }

    onConfirm(data)
  }

  // 重置状态
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setAction(1)
      setRefundPrice(refund.refund_price.toString())
      setRefuseReason('')
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>处理退款申请</DialogTitle>
          <DialogDescription>
            订单号：{refund.order_id} | 退款金额：¥{refund.refund_price.toFixed(2)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* 操作选择 */}
          <div className="space-y-3">
            <Label>处理方式</Label>
            <RadioGroup
              value={action.toString()}
              onValueChange={(value) => setAction(Number(value) as 1 | 2)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="1" id="agree" />
                <Label htmlFor="agree" className="cursor-pointer">
                  同意退款
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="2" id="reject" />
                <Label htmlFor="reject" className="cursor-pointer">
                  拒绝退款
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* 退款金额 */}
          {action === 1 && (
            <div className="space-y-2">
              <Label>退款金额（元）</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={refund.refund_price}
                value={refundPrice}
                onChange={(e) => setRefundPrice(e.target.value)}
                placeholder="请输入退款金额"
              />
              <p className="text-xs text-muted-foreground">
                最大可退金额：¥{refund.refund_price.toFixed(2)}
              </p>
            </div>
          )}

          {/* 拒绝原因 */}
          {action === 2 && (
            <div className="space-y-2">
              <Label>拒绝原因 *</Label>
              <Textarea
                placeholder="请输入拒绝退款的原因"
                value={refuseReason}
                onChange={(e) => setRefuseReason(e.target.value)}
                rows={3}
              />
            </div>
          )}

          {/* 提示信息 */}
          {action === 2 && (
            <div className="flex items-start gap-2 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>拒绝退款后，用户可能会发起投诉，请谨慎操作。</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            取消
          </Button>
          <Button
            variant={action === 2 ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={!isValid() || isLoading}
          >
            {isLoading ? '处理中...' : action === 1 ? '确认退款' : '确认拒绝'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
